import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="terms-conditions-page">
      {/* Header */}
      <div className="border-b border-[#0A0A0A]/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-[#525252] hover:text-[#0A0A0A] transition-colors w-fit">
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-[#0A0A0A] mb-2">Terms and Conditions</h1>
        <p className="text-[#525252] mb-8">Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="prose prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">1. Agreement to Terms</h2>
            <p className="text-[#525252] leading-relaxed">
              By accessing or using Sol Digital's website and services, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our services. These terms are governed by the laws of India, including but not limited to the Information Technology Act, 2000, the Consumer Protection Act, 2019, and the Indian Contract Act, 1872.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">2. Definitions</h2>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li><strong>"Service"</strong> refers to Sol Digital's digital business card platform and NFC card products</li>
              <li><strong>"User"</strong> refers to any individual or entity using our services</li>
              <li><strong>"Digital Profile"</strong> refers to the online business card profile created on our platform</li>
              <li><strong>"NFC Card"</strong> refers to the physical near-field communication card product</li>
              <li><strong>"Subscription"</strong> refers to the annual service plan for digital profile hosting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">3. Services Description</h2>
            <p className="text-[#525252] leading-relaxed mb-4">Sol Digital provides:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Digital business card creation and hosting</li>
              <li>Physical NFC cards that link to digital profiles</li>
              <li>Lead capture and analytics features</li>
              <li>Annual subscription services for profile hosting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">4. User Account</h2>
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">4.1 Account Creation</h3>
            <p className="text-[#525252] leading-relaxed mb-4">
              To use our services, you must create an account with accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials.
            </p>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">4.2 Account Responsibilities</h3>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>You must be at least 18 years old to create an account</li>
              <li>You are responsible for all activities under your account</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>You may not transfer your account to another person</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">5. Pricing and Payments</h2>
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">5.1 Pricing</h3>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>NFC Card (one-time): ₹1,000 (inclusive of all taxes)</li>
              <li>Annual Subscription: ₹365/year (inclusive of all taxes)</li>
            </ul>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-4">5.2 Payment Terms</h3>
            <p className="text-[#525252] leading-relaxed">
              All payments are processed securely through Razorpay. We accept UPI, credit cards, debit cards, net banking, and other payment methods supported by Razorpay. All prices are in Indian Rupees (INR) and include applicable GST.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">6. Subscription Terms</h2>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Subscriptions are valid for 365 days from the date of purchase</li>
              <li>Your digital profile will be deactivated if the subscription is not renewed</li>
              <li>Renewal reminders will be sent 30 days before expiration</li>
              <li>No prorated refunds are provided for early cancellation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">7. User Content</h2>
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">7.1 Content Ownership</h3>
            <p className="text-[#525252] leading-relaxed mb-4">
              You retain ownership of all content you upload to your digital profile, including photos, logos, and documents. By uploading content, you grant Sol Digital a non-exclusive license to display and distribute this content as part of the service.
            </p>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">7.2 Prohibited Content</h3>
            <p className="text-[#525252] leading-relaxed mb-4">You agree not to upload content that:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Violates any applicable laws or regulations</li>
              <li>Infringes on intellectual property rights</li>
              <li>Contains malware, viruses, or harmful code</li>
              <li>Is defamatory, obscene, or offensive</li>
              <li>Promotes illegal activities or violence</li>
              <li>Impersonates another person or entity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">8. Intellectual Property</h2>
            <p className="text-[#525252] leading-relaxed">
              The Sol Digital platform, including its design, features, and functionality, is owned by Sol Digital and protected by copyright, trademark, and other intellectual property laws of India. You may not copy, modify, or distribute our platform without written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">9. Limitation of Liability</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              To the maximum extent permitted by Indian law:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Sol Digital shall not be liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount paid by you in the last 12 months</li>
              <li>We do not guarantee uninterrupted or error-free service</li>
              <li>We are not responsible for third-party services or links on user profiles</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">10. Indemnification</h2>
            <p className="text-[#525252] leading-relaxed">
              You agree to indemnify and hold Sol Digital harmless from any claims, damages, or expenses arising from your use of our services, violation of these terms, or infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">11. Account Termination</h2>
            <p className="text-[#525252] leading-relaxed mb-4">We may suspend or terminate your account if:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>You violate these Terms and Conditions</li>
              <li>Your payment fails or subscription expires</li>
              <li>You engage in fraudulent or illegal activities</li>
              <li>Your account remains inactive for an extended period</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">12. Dispute Resolution</h2>
            <p className="text-[#525252] leading-relaxed">
              Any disputes arising from these terms shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996. The arbitration shall be conducted in English and held in India. The decision of the arbitrator shall be final and binding.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">13. Governing Law</h2>
            <p className="text-[#525252] leading-relaxed">
              These Terms and Conditions are governed by and construed in accordance with the laws of India. Any legal proceedings shall be subject to the exclusive jurisdiction of the courts in India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">14. Changes to Terms</h2>
            <p className="text-[#525252] leading-relaxed">
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Continued use of our services after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">15. Contact Information</h2>
            <p className="text-[#525252] leading-relaxed">
              For any questions regarding these Terms and Conditions, please contact us:
            </p>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 mt-4">
              <p className="text-[#0A0A0A] font-medium">Sol Digital</p>
              <p className="text-[#525252]">Email: support@soldigital.in</p>
              <p className="text-[#525252]">Address: India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
